/**
 * Uploading several files into a shared folder, followed in one toast: progress over all of them,
 * a Cancel button, and at the end what was uploaded and what was not.
 */
import React from 'react'
import {Id, toast} from 'react-toastify'
import {toastActionableConfig, toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import type {FileStore} from '../widgets/core/storage'
import {runLimited, UPLOAD_CONCURRENCY} from '../widgets/core/storage/uploadLimits'
import {shareErrorMessage} from './errors'
import {isShareUploadAborted} from './shareUpload'
import './sharing.scss'

export interface ShareUploadItem {
  /** Relative to the shared folder. */
  path: string
  file: File
}

export interface ShareUploadBatchResult {
  uploaded: string[]
  failed: {path: string; error: unknown}[]
  cancelled: boolean
}

/** Progress redraws at most this often. */
const REDRAW_MS = 250
const MAX_LISTED = 5

const filesLabel = (count: number) => (count === 1 ? '1 file' : `${count.toLocaleString()} files`)

const UploadProgress: React.FC<{text: string; onCancel?: () => void}> = ({text, onCancel}) => (
  <div className='ob-sharing-upload-toast' data-testid='share-upload-toast'>
    <span className='ob-sharing-upload-toast__text'>{text}</span>
    {onCancel && (
      <button
        type='button'
        className='btn btn-sm btn-light'
        onClick={(e) => {
          e.stopPropagation()
          onCancel()
        }}
        data-testid='share-upload-cancel'
      >
        Cancel
      </button>
    )}
  </div>
)

/**
 * Uploads `items` through `store` (a shared folder's), with one toast for the batch. `where` names
 * the destination in the toast (“Team data”). Files go {@link UPLOAD_CONCURRENCY} at a time, as for
 * any upload (#372); a large one also sends its parts 4 at a time. The 1,000-file cap is checked by
 * the browser before the batch starts.
 */
export async function uploadBatchToShare(
  store: FileStore,
  items: ShareUploadItem[],
  where: string
): Promise<ShareUploadBatchResult> {
  const result: ShareUploadBatchResult = {uploaded: [], failed: [], cancelled: false}
  if (items.length === 0) {
    return result
  }
  const controller = new AbortController()
  const cancel = () => controller.abort()
  const total = items.reduce((sum, item) => sum + item.file.size, 0)
  const sent = new Map<string, number>()
  const title = `Uploading ${filesLabel(items.length)} to ${where}`

  const toastId: Id = toast(<UploadProgress text={`${title}…`} onCancel={cancel} />, {
    ...toastDefaultConfig,
    autoClose: false,
    closeOnClick: false,
    closeButton: false,
    hideProgressBar: false,
    progress: 0,
  })

  let lastDraw = 0
  const draw = (force = false) => {
    const now = Date.now()
    if (!force && now - lastDraw < REDRAW_MS) return
    lastDraw = now
    const done = Array.from(sent.values()).reduce((sum, n) => sum + n, 0)
    // Zero-byte files still count: progress by files when there are no bytes to count.
    const progress = total > 0 ? done / total : result.uploaded.length / items.length
    toast.update(toastId, {
      render: (
        <UploadProgress text={`${title}… ${Math.floor(progress * 100)}%`} onCancel={cancel} />
      ),
      // react-toastify closes a toast whose progress reaches 1; the end is reported below instead.
      progress: Math.min(progress, 0.99),
    })
  }

  // Each task records its own outcome, so runLimited never stops early. After Cancel, the tasks
  // still waiting end at once: an aborted signal fails an upload before it sends anything.
  const upload = async (item: ShareUploadItem) => {
    if (controller.signal.aborted) {
      result.cancelled = true
      return
    }
    try {
      await store.upload(item.path, item.file, {
        signal: controller.signal,
        onProgress: (loaded) => {
          sent.set(item.path, loaded)
          draw()
        },
      })
      sent.set(item.path, item.file.size)
      result.uploaded.push(item.path)
      draw()
    } catch (error) {
      if (isShareUploadAborted(error) || controller.signal.aborted) {
        result.cancelled = true
      } else {
        getLogger().warn(`Upload to shared folder failed: ${item.path}`, error)
        result.failed.push({path: item.path, error})
      }
    }
  }
  await runLimited(
    items.map((item) => () => upload(item)),
    UPLOAD_CONCURRENCY
  )
  result.cancelled = result.cancelled || controller.signal.aborted

  if (result.failed.length > 0) {
    const names = result.failed.map((f) => f.path.split('/').pop() || f.path)
    const shown = names.slice(0, MAX_LISTED).join(', ')
    toast.update(toastId, {
      render: (
        <div data-testid='share-upload-failed'>
          <div className='fw-bold'>
            Couldn't upload {filesLabel(result.failed.length)} to {where}
            {result.uploaded.length > 0 ? ` (${filesLabel(result.uploaded.length)} uploaded)` : ''}.
          </div>
          <div>
            {shown}
            {names.length > MAX_LISTED ? ` and ${names.length - MAX_LISTED} more` : ''}
          </div>
          <div>{shareErrorMessage(result.failed[0].error)}</div>
        </div>
      ),
      type: 'error',
      progress: undefined,
      ...toastActionableConfig,
      closeButton: true,
    })
  } else if (result.cancelled) {
    toast.update(toastId, {
      render: `Upload cancelled: ${result.uploaded.length.toLocaleString()} of ${filesLabel(
        items.length
      )} uploaded.`,
      type: 'info',
      progress: undefined,
      ...toastDefaultConfig,
      closeButton: true,
    })
  } else {
    toast.update(toastId, {
      render: `Uploaded ${filesLabel(result.uploaded.length)} to ${where}.`,
      type: 'success',
      progress: undefined,
      ...toastDefaultConfig,
      closeButton: true,
    })
  }
  return result
}
